import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'Are there long-term contracts?',
  answer: 'No. All our packages are month-to-month. We believe in earning your business every month, not locking you in. You can cancel anytime with 30 days notice.'
}, {
  question: 'Is the ad budget included in the monthly fee?',
  answer: "No, your ad spend is separate from our management fee. This gives you full control over your budget. We'll recommend a starting budget based on your market and goals."
}, {
  question: 'How often do I get reports?',
  answer: 'All clients receive monthly performance reports. Full Automation Suite clients also get monthly strategy calls to review results and plan next steps.'
}, {
  question: 'What kind of support do I get?',
  answer: 'All clients get email and chat support during business hours. Never Miss A Lead and Full Automation Suite clients get priority response times.'
}, {
  question: 'How quickly can I get started?',
  answer: 'Most clients are fully set up within 5-7 business days. We handle all the technical setup so you can focus on running your business.'
}, {
  question: 'Do I need to be tech-savvy?',
  answer: "Not at all. We handle everything technical. You'll get a simple dashboard and mobile app to manage your leads and appointments."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-1473">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-1474">
        <span className="text-lg font-semibold text-gray-900" data-id="element-1475">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-1476">
          <ChevronDownIcon className="w-5 h-5" data-id="element-1477" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-1478">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1479">
            <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-1480">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function PricingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1481">
      <SEOHead title="Pricing | Marketing Packages for Local Businesses | Viridia Analytics Phoenix" description="Transparent, month-to-month pricing for ad campaign management, AI lead conversion, and web design. No long-term contracts. Packages starting for Phoenix local service businesses." canonical="/pricing" keywords="marketing agency pricing, ad management pricing Phoenix, marketing automation cost, web design pricing, local business marketing packages" faq={faqs} data-id="element-1482" />
      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-1483">
        <ScrollReveal data-id="element-1484">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1485">
            CRM & AUTOMATION
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-1486">
            Automation Systems That{' '}
            <span className="text-viridia" data-id="element-1487">Run Your Business</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-1488">
            Month-to-month systems that capture leads, automate follow-up, and
            keep your business organized — so you can focus on the work.
          </p>
        </ScrollReveal>
      </section>

      {/* PRICING CARDS SECTION */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1489">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-1490">
          {/* Card 1: Business Essentials */}
          <ScrollReveal delay={0.1} data-id="element-1491">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1492">
              <div className="mb-8" data-id="element-1493">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1494">
                  Business Essentials
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-1495">
                  The solid foundation: CRM, scheduling, and professional
                  billing.
                </p>
              </div>
              <div className="mb-8" data-id="element-1496">
                <span className="text-5xl font-extrabold text-white" data-id="element-1497">$497</span>
                <span className="text-gray-500 font-medium" data-id="element-1498">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-1499">
                {['CRM Setup & Contact Management', 'Online Booking / Calendar Integration', 'Professional Branded Invoicing & Revenue Tracking (PayPal / Square / Stripe sync)', 'QuickBooks & Local Billing Integration', 'Lead Capture Workflows', 'Yelp Auto-Responder (1 auto-response per new lead)', 'GHL Mobile App Access'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1500">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1501" />
                    <span className="text-gray-300 text-sm" data-id="element-1502">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-1503">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 2: Never Miss A Lead (MOST POPULAR) */}
          <ScrollReveal delay={0.2} data-id="element-1504">
            <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-1505">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-1506">
                Most Popular
              </div>
              <div className="mb-8 mt-2" data-id="element-1507">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1508">
                  Never Miss A Lead
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-1509">
                  For busy contractors who lose jobs every time a call goes
                  unanswered.
                </p>
              </div>
              <div className="mb-8" data-id="element-1510">
                <span className="text-5xl font-extrabold text-white" data-id="element-1511">$897</span>
                <span className="text-gray-500 font-medium" data-id="element-1512">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-1513">
                <li className="flex items-start" data-id="element-1514">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1515" />
                  <span className="text-white font-medium text-sm" data-id="element-1516">
                    Everything in Business Essentials
                  </span>
                </li>
                {['AI Voice Receptionist (24/7 call answering)', 'Missed Call Text-Back Automation', 'A2P Registration & Business Number Setup', 'Yelp AI Conversational Chatbot', 'Unified Communications Hub'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1517">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1518" />
                    <span className="text-gray-300 text-sm" data-id="element-1519">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25" data-id="element-1520">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 3: Full Automation Suite */}
          <ScrollReveal delay={0.3} data-id="element-1521">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1522">
              <div className="mb-8" data-id="element-1523">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1524">
                  Full Automation Suite
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-1525">
                  The full system. Your business runs on autopilot.
                </p>
              </div>
              <div className="mb-8" data-id="element-1526">
                <span className="text-5xl font-extrabold text-white" data-id="element-1527">
                  $1,497
                </span>
                <span className="text-gray-500 font-medium" data-id="element-1528">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-1529">
                <li className="flex items-start" data-id="element-1530">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1531" />
                  <span className="text-white font-medium text-sm" data-id="element-1532">
                    Everything in Never Miss A Lead
                  </span>
                </li>
                {['AI Conversational Chatbot (all applicable platforms & website)', 'Reputation Management', 'Review Automation', 'Pipeline Automation', 'Monthly Strategy Calls'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1533">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1534" />
                    <span className="text-gray-300 text-sm" data-id="element-1535">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-1536">
                Get Started
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AD MANAGEMENT ADD-ON */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1537">
        <ScrollReveal direction="up" data-id="element-1538">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1539">
            <div data-id="element-1540">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1541">
                Need Ad Management Too?
              </h3>
              <p className="text-gray-400 max-w-2xl" data-id="element-1542">
                Ad campaign management can be added to any package. Pricing
                depends on platforms, ad spend, and scope. Book a call to get a
                custom quote.
              </p>
            </div>
            <Link to="/contact" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-1543">
              Get a Custom Ad Quote <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1544" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* WEBSITE PRICING SECTION */}
      <section id="web-design" className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1545">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1546">
          <ScrollReveal data-id="element-1547">
            <div className="text-center mb-16" data-id="element-1548">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1549">
                WEB DESIGN & SEO
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4" data-id="element-1550">
                Website Packages
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-1551">
                High-converting, mobile-first websites built for local service
                businesses. One-time investment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-1552">
            {/* Starter Site */}
            <ScrollReveal delay={0.1} data-id="element-1553">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1554">
                <div className="mb-8" data-id="element-1555">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1556">
                    Starter Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-1557">
                    The essentials to get your business online and looking
                    professional.
                  </p>
                </div>
                <div className="mb-8" data-id="element-1558">
                  <span className="text-4xl font-extrabold text-white" data-id="element-1559">
                    $1,200–$1,800
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-1560">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-1561">
                  {['3 pages (Home, Services, Contact)', 'Mobile responsive', 'Contact form', 'On-page SEO', 'Google Analytics setup'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1562">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1563" />
                      <span className="text-gray-300 text-sm" data-id="element-1564">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-1565">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-1566">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-1567">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Growth Site (MOST POPULAR) */}
            <ScrollReveal delay={0.2} data-id="element-1568">
              <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-1569">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-1570">
                  Most Popular
                </div>
                <div className="mb-8 mt-2" data-id="element-1571">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1572">
                    Growth Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-1573">
                    Built to convert traffic into leads and dominate local
                    search.
                  </p>
                </div>
                <div className="mb-8" data-id="element-1574">
                  <span className="text-4xl font-extrabold text-white" data-id="element-1575">
                    $2,500–$3,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-1576">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-1577">
                  {['5–7 pages', 'Custom design', 'Service-specific pages', 'Review/trust section', 'Conversion CTAs', 'On-page SEO baked in'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1578">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1579" />
                      <span className="text-gray-300 text-sm" data-id="element-1580">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-1581">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25 mb-3" data-id="element-1582">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-400 text-xs" data-id="element-1583">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Premium Site */}
            <ScrollReveal delay={0.3} data-id="element-1584">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-1585">
                <div className="mb-8" data-id="element-1586">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1587">
                    Premium Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-1588">
                    The ultimate digital storefront for established businesses.
                  </p>
                </div>
                <div className="mb-8" data-id="element-1589">
                  <span className="text-4xl font-extrabold text-white" data-id="element-1590">
                    $4,000–$6,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-1591">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-1592">
                  {['8–12 pages', 'Full custom design', 'Blog setup', 'Multiple service pages', 'Schema markup', 'Lead capture', 'Speed optimization'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1593">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1594" />
                      <span className="text-gray-300 text-sm" data-id="element-1595">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-1596">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-1597">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-1598">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO ADD-ONS */}
      <section className="py-16 bg-viridia-black" data-id="element-1599">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1600">
          <ScrollReveal data-id="element-1601">
            <div className="text-center mb-12" data-id="element-1602">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-1603">
                Local SEO Add-Ons
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-1604">
                Ongoing optimization to ensure your new website actually gets
                found by local customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1605">
            {/* Basic */}
            <ScrollReveal delay={0.1} data-id="element-1606">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-1607">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-1608">
                  Basic Local SEO
                </h4>
                <div className="mb-6" data-id="element-1609">
                  <span className="text-2xl font-bold text-viridia" data-id="element-1610">
                    $300–$500
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-1611">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-1612">
                  {['GBP optimization', 'Citations', 'Monthly report'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1613">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-1614" />
                        <span className="text-gray-300 text-sm" data-id="element-1615">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Standard */}
            <ScrollReveal delay={0.2} data-id="element-1616">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-1617">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-1618">
                  Standard Local SEO
                </h4>
                <div className="mb-6" data-id="element-1619">
                  <span className="text-2xl font-bold text-viridia" data-id="element-1620">
                    $500–$800
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-1621">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-1622">
                  <li className="flex items-start" data-id="element-1623">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-1624" />
                    <span className="text-white font-medium text-sm" data-id="element-1625">
                      Everything in Basic
                    </span>
                  </li>
                  {['On-page SEO', 'Schema markup', 'Review management'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1626">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-1627" />
                        <span className="text-gray-300 text-sm" data-id="element-1628">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Full */}
            <ScrollReveal delay={0.3} data-id="element-1629">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-1630">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-1631">
                  Full Local SEO
                </h4>
                <div className="mb-6" data-id="element-1632">
                  <span className="text-2xl font-bold text-viridia" data-id="element-1633">
                    $800–$1,200
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-1634">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-1635">
                  <li className="flex items-start" data-id="element-1636">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-1637" />
                    <span className="text-white font-medium text-sm" data-id="element-1638">
                      Everything in Standard
                    </span>
                  </li>
                  {['Citation building', 'GBP posts & content', 'City pages', 'Competitor tracking'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-1639">
                      <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-1640" />
                      <span className="text-gray-300 text-sm" data-id="element-1641">{feature}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ONE-TIME LOCAL PRESENCE SERVICES */}
      <section className="py-16 bg-viridia-black border-t border-gray-800" data-id="element-1642">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1643">
          <ScrollReveal data-id="element-1644">
            <div className="text-center mb-12" data-id="element-1645">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1646">
                ONE-TIME SERVICE
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-1647">
                Google Business Profile Launch & Verification
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-1648">
                If your Google Business Profile isn't set up or verified
                correctly, you may not be showing up where local customers
                search. We handle the setup and verification process so your
                business is properly listed, visible, and ready to support
                reviews, calls, and local trust.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1} data-id="element-1649">
            <div className="bg-viridia-surface rounded-2xl p-8 md:p-10 border border-gray-800" data-id="element-1650">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8" data-id="element-1651">
                <div data-id="element-1652">
                  <h4 className="text-2xl font-bold text-white mb-1" data-id="element-1653">
                    GBP Launch & Verification
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-1654">
                    A strong first step for businesses not yet properly set up
                    on Google. Pairs well with our automation, ad, and website
                    services.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right" data-id="element-1655">
                  <span className="text-4xl font-extrabold text-white" data-id="element-1656">
                    $397
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-1657">
                    one-time
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-1658">
                {['Create or claim your Google Business Profile', 'Guide or manage the verification process', 'Add correct business name, address & info', 'Set service area & local coverage correctly', 'Choose the right business categories', 'Add business description', 'Add website, phone, hours & core details', 'Upload logo & branded image placeholders (if assets available)', 'Final check that profile is live & properly configured'].map((feature, i) => <div key={i} className="flex items-start" data-id="element-1659">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1660" />
                    <span className="text-gray-300 text-sm" data-id="element-1661">{feature}</span>
                  </div>)}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-gray-800" data-id="element-1662">
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 text-sm font-bold text-viridia-black bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 w-full sm:w-auto" data-id="element-1663">
                  Get Started
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-1664" />
                </Link>
                <p className="text-gray-500 text-xs text-center sm:text-left" data-id="element-1665">
                  This is Viridia's service fee for setup and verification help.
                  Google does not charge for creating a Business Profile.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* HUBSPOT SEO CERTIFICATION */}
      <section className="py-16 bg-viridia-black" data-id="element-1666">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1667">
          <ScrollReveal data-id="element-1668">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-1669">
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center" data-id="element-1670">
                <div className="relative group" data-id="element-1671">
                  <div className="absolute -inset-1 bg-viridia/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1672"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot Academy SEO Certification — Kevin Rogers" loading="lazy" className="relative w-64 md:w-72 rounded-lg shadow-xl border border-gray-700" data-id="element-1673" />
                </div>
              </div>
              <div className="text-center md:text-left" data-id="element-1674">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-1675">
                  <svg viewBox="0 0 512 512" className="w-8 h-8" aria-label="HubSpot logo" data-id="element-1676">
                    <path fill="#FF7A59" d="M380.48 256c0-19.2-9.6-36.48-24.96-47.04V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v40.32c-5.28-1.92-10.88-3.2-16.64-3.52V136c0-17.28-14.08-31.36-31.36-31.36-17.28 0-31.36 14.08-31.36 31.36v62.4c-5.76.32-11.36 1.6-16.64 3.52V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v47.36C186.72 219.52 177.12 236.8 177.12 256c0 29.44 19.52 54.4 46.24 62.72l-24.64 24.64c-3.84 3.84-3.84 10.08 0 13.92 1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88l31.04-31.04c5.28 1.6 10.88 2.56 16.64 2.88v44.48c0 17.28 14.08 31.36 31.36 31.36 17.28 0 31.36-14.08 31.36-31.36v-44.48c5.76-.32 11.36-1.28 16.64-2.88l31.04 31.04c1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88c3.84-3.84 3.84-10.08 0-13.92l-24.64-24.64C360.96 310.4 380.48 285.44 380.48 256zM278.72 296.96c-22.56 0-40.96-18.4-40.96-40.96s18.4-40.96 40.96-40.96 40.96 18.4 40.96 40.96-18.4 40.96-40.96 40.96z" data-id="element-1677" />
                  </svg>
                  <span className="text-sm font-bold text-[#FF7A59] tracking-wide uppercase" data-id="element-1678">
                    HubSpot Academy
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-1679">
                  SEO Certified
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-1680">
                  Our team is HubSpot SEO Certified — trained and tested on
                  technical SEO, on-page optimization, link building, and search
                  strategy. Not guesswork. Proven methodology.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FREELANCER COMPARISON */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1681">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1682">
          <ScrollReveal data-id="element-1683">
            <div className="text-center mb-12" data-id="element-1684">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1685">
                Why Not Just Hire a Freelancer?
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1686">
                You get what you pay for. We build systems that generate
                revenue, not just pretty brochures.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1687">
            <ScrollReveal delay={0.1} data-id="element-1688">
              <div className="bg-viridia-surface/50 rounded-2xl p-8 border border-red-900/30 h-full" data-id="element-1689">
                <h3 className="text-xl font-bold text-gray-300 mb-6 flex items-center" data-id="element-1690">
                  <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mr-3" data-id="element-1691">
                    <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-1692" />
                  </span>
                  The Average Freelancer
                </h3>
                <ul className="space-y-4" data-id="element-1693">
                  {['Cheap, bloated templates', 'No marketing or conversion strategy', 'Zero accountability for results', 'Disappears after launch'].map((item, i) => <li key={i} className="flex items-start" data-id="element-1694">
                      <XCircleIcon className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 opacity-70" data-id="element-1695" />
                      <span className="text-gray-400" data-id="element-1696">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1697">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-viridia/30 h-full shadow-lg shadow-viridia/5" data-id="element-1698">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center" data-id="element-1699">
                  <span className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center mr-3" data-id="element-1700">
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-1701" />
                  </span>
                  Viridia Analytics
                </h3>
                <ul className="space-y-4" data-id="element-1702">
                  {['Conversion-focused, custom builds', 'Deep local SEO knowledge', 'Ongoing support and optimization', 'Built specifically for your market'].map((item, i) => <li key={i} className="flex items-start" data-id="element-1703">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-1704" />
                      <span className="text-gray-200 font-medium" data-id="element-1705">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-1706">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1707">
          <ScrollReveal data-id="element-1708">
            <div className="text-center mb-16" data-id="element-1709">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1710">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600" data-id="element-1711">
                Everything you need to know about our packages and billing.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-1712">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100" data-id="element-1713">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1714" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-1715">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1716"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-1717">
          <ScrollReveal data-id="element-1718">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1719">
              Not Sure Which Package Is Right?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1720">
              Book a free call and we'll recommend the perfect plan based on
              your business size and goals.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-1721">
              Book Your Free Consultation
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}